import { getPaginatedApiParams, PaginatedApiArg } from 'lib-shared-api-utils';
import { AsyncStatus, PaginatedApiResponse } from 'lib-shared-types-api';
import { searchStringToParams } from 'lib-shared-url';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import {
  ChangeArg,
  GetItems,
  MergeStrategy,
  MergeStrategyFn,
  RemoveItem,
  Search,
  UpdateItem,
  UseLazyPaginatedOptions,
  UsePaginatedResult,
} from './useLazyPaginatedEndpoint.types';

/** The merge strategies and their merge functions. */
const mergeStrategyFn: Record<MergeStrategy, MergeStrategyFn> = {
  prepend: (items, next) => [...next, ...items],
  append: (items, next) => [...items, ...next],
  replace: (_items, next) => next,
};

/**
 * Represents a hook that simplifies working with paginated endpoints. It
 * encapsulates the items, while exposes functions for navigating the pages,
 * changing the arg, re-fetching, updating an item, and more.
 */
export const useLazyPaginatedEndpoint = <
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TItem,
>({
  endpoint,
  infinite = false,
  itemsSelector,
  totalSelector,
}: UseLazyPaginatedOptions<
  TPaginatedApiArg,
  TPaginatedApiResponse,
  TItem
>): UsePaginatedResult<TItem, TPaginatedApiArg, TPaginatedApiResponse> => {
  const dispatch = useAppDispatch();
  // Data.
  const [items, setItems] = useState<readonly TItem[]>([]);
  const [data, setData] = useState<TPaginatedApiResponse['data']>({});
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('uninitialized');
  // Pagination.
  const [total, setTotal] = useState<number>();
  const [nextPagePath, setNextPagePath] = useState<string | null>(null);
  const [previousPagePath, setPreviousPagePath] = useState<string | null>(null);
  const [isPaginating, setIsPaginating] = useState(false);
  // State.
  const timeoutRef = useRef<number>();
  const skipFetchRef = useRef<boolean>(false);
  const debounceTimeRef = useRef<number>(0);
  const [arg, setArg] = useState<Exclude<TPaginatedApiArg, string>>();
  const [debouncedArg, setDebouncedArg] =
    useState<Exclude<TPaginatedApiArg, string>>();

  /** Change the arg, which in effect fetches the items anew. */
  const changeArg = useCallback<ChangeArg<TPaginatedApiArg>>(
    (newArg, { skipFetch = false, debounceTime = 0 } = {}) => {
      skipFetchRef.current = skipFetch;
      debounceTimeRef.current = debounceTime;
      setArg(
        (prevArg) =>
          ({
            ...(prevArg ?? {}),
            ...newArg,
          } as Exclude<TPaginatedApiArg, string>),
      );
    },
    [],
  );

  /** Gets the items. */
  const getItems = useCallback<GetItems>(
    async ({ page, mergeStrategy = 'replace' } = {}) => {
      // We batch the state updates as React would otherwise schedule them
      // one-by-one when set in an async function - i.e. without this the
      // consumer would see a pending async status with pagination false and
      // then in next cycle see pagination true when in fact these two values
      // should be updated together. https://github.com/reactwg/react-18/discussions/21
      unstable_batchedUpdates(() => {
        setAsyncStatus('pending');
        if (page) setIsPaginating(true);
      });

      const result = dispatch(
        endpoint.initiate((page as unknown as TPaginatedApiArg) ?? arg, {
          forceRefetch: true,
        }),
      );
      result.unsubscribe();

      const { data } = await result;
      if (data) {
        unstable_batchedUpdates(() => {
          setData(data.data);
          setItems(mergeStrategyFn[mergeStrategy](items, itemsSelector(data)));
          setAsyncStatus('fulfilled');
          setIsPaginating(false);
          setNextPagePath(data.meta.next_page);
          setPreviousPagePath(data.meta.prev_page);

          if (totalSelector) {
            setTotal(totalSelector(data));
          }
        });

        if (page) {
          const search = page.substring(page.indexOf('?') + 1);
          const params = searchStringToParams(search);
          const paginatedApiParams = getPaginatedApiParams(params);

          delete params['page'];
          delete params['page_size'];
          delete params['pageSize'];
          delete params['page_number'];
          delete params['count'];

          changeArg(
            { ...params, ...paginatedApiParams } as Exclude<
              TPaginatedApiArg,
              string
            >,
            { skipFetch: true },
          );
        }
      } else {
        unstable_batchedUpdates(() => {
          setAsyncStatus('rejected');
          if (page) setIsPaginating(false);
        });
      }
    },
    [arg, endpoint, items, changeArg, dispatch, itemsSelector, totalSelector],
  );

  /** Get the items for the next page. */
  const nextPage = useCallback(() => {
    if (!nextPagePath) {
      throw Error('Next page does not exist');
    }

    getItems({
      page: nextPagePath,
      mergeStrategy: infinite ? 'append' : 'replace',
    });
  }, [getItems, infinite, nextPagePath]);

  /** Get the items for the previous page. */
  const previousPage = useCallback(() => {
    if (!previousPagePath) {
      throw Error('Previous page does not exist');
    }

    getItems({
      page: previousPagePath,
      mergeStrategy: infinite ? 'prepend' : 'replace',
    });
  }, [getItems, infinite, previousPagePath]);

  /** Updates an item. */
  const updateItem = useCallback<UpdateItem<TItem>>(
    (itemToUpdate, compareFn) => {
      setItems((items) =>
        items.map((item) =>
          compareFn(item, itemToUpdate) ? itemToUpdate : item,
        ),
      );
    },
    [],
  );

  /** Removes an item. */
  const removeItem = useCallback<RemoveItem<TItem>>(
    (itemToRemove, compareFn) => {
      setItems((items) =>
        items.filter((item) => !compareFn(item, itemToRemove)),
      );
    },
    [],
  );

  /** Re-fetches the items anew. */
  const refetchItems = useCallback(() => {
    return getItems({
      mergeStrategy: 'replace',
    });
  }, [getItems]);

  /** Searches the items, which in effect fetches the items anew. */
  const search = useCallback<Search>(
    (searchTerm, options = { debounceTime: 300 }) => {
      changeArg(
        {
          searchTerm,
          offset: undefined,
          limit: undefined,
        } as Exclude<TPaginatedApiArg, string>,
        options,
      );
    },
    [changeArg],
  );

  useEffect(() => {
    if (arg) {
      if (skipFetchRef.current) {
        // Temporary fix to make sure arg is correctly propagated to the consumer.
        // TODO: [TEAMS-13085] Improve useLazyPaginatedEndpoint consumption
        setDebouncedArg(arg);
        return;
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // When params are changed, the list needs to be replaced with the new
      // results, hence the replace merge strategy.
      const update = () => {
        getItems({ mergeStrategy: 'replace' });
        setDebouncedArg(arg);
      };

      if (debounceTimeRef.current > 0) {
        timeoutRef.current = window.setTimeout(update, debounceTimeRef.current);
      } else {
        update();
      }
    }
    // Excluding `getItems` from the dependencies array to prevent an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg]);

  return {
    items,
    data,
    total,
    asyncStatus,
    arg: debouncedArg,
    isPaginating,
    hasPreviousPage: previousPagePath !== null,
    hasNextPage: nextPagePath !== null,
    previousPage,
    nextPage,
    changeArg,
    search,
    updateItem,
    removeItem,
    refetchItems,
  };
};
